import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLE_DESCRIPTIONS, ROLE_LABELS, type InvitePreview, type SessionState } from '@hoopify/shared';
import { api, errorMessage } from '../lib/api';
import { useSession, useSetSession } from '../lib/session';
import { AuthLayout } from '../components/AuthLayout';
import { Alert, Button, LoadingBlock } from '../components/ui';

export function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setSession = useSetSession();
  const { data: session } = useSession();
  const invite = useQuery({ queryKey: ['invite', token], queryFn: () => api.get<InvitePreview>(`/invites/${token}`) });

  const accept = useMutation({
    mutationFn: () => api.post<{ teamId: string }>(`/invites/${token}/accept`),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      navigate(`/teams/${r.teamId}`, { replace: true });
    },
  });
  const signOut = useMutation({ mutationFn: () => api.post<SessionState>('/auth/logout'), onSuccess: setSession });

  if (invite.isLoading) {
    return (
      <AuthLayout title="Opening your invite…">
        <LoadingBlock />
      </AuthLayout>
    );
  }
  if (invite.isError || !invite.data) {
    return (
      <AuthLayout title="This invite can’t be used">
        <Alert>{errorMessage(invite.error)}</Alert>
        <Link to="/" className="btn-secondary mt-5 w-full">
          Go to Hoopify
        </Link>
      </AuthLayout>
    );
  }

  const inv = invite.data;
  const here = `/invite/${token}`;
  const user = session?.status === 'signed_in' ? session.user : null;

  return (
    <AuthLayout
      title={`Join ${inv.teamName}`}
      description={
        <>
          {inv.invitedBy} invited <strong className="font-semibold text-ink">{inv.email}</strong> to join as {ROLE_LABELS[inv.role].toLowerCase()}. {ROLE_DESCRIPTIONS[inv.role]}
        </>
      }
    >
      {inv.expired ? (
        <Alert tone="warn">This invite has expired. Ask {inv.invitedBy} to send you a new one.</Alert>
      ) : user && user.email === inv.email ? (
        <div className="space-y-3">
          {accept.isError && <Alert>{errorMessage(accept.error)}</Alert>}
          <Button loading={accept.isPending} onClick={() => accept.mutate()} className="btn-primary btn-lg w-full">
            Join {inv.teamName}
          </Button>
        </div>
      ) : user ? (
        <div className="space-y-4">
          <Alert tone="warn">
            You’re signed in as {user.email}, but this invite is for {inv.email}. Sign out, then sign in or create an account with {inv.email}.
          </Alert>
          <Button variant="secondary" loading={signOut.isPending} onClick={() => signOut.mutate()} className="btn-secondary w-full">
            Sign out
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <Link to={`/signup?invite=${encodeURIComponent(token)}`} className="btn-primary btn-lg w-full">
            Create an account and join
          </Link>
          <Link to={`/login?email=${encodeURIComponent(inv.email)}&next=${encodeURIComponent(here)}`} className="btn-secondary w-full">
            I already have an account
          </Link>
        </div>
      )}
    </AuthLayout>
  );
}
